import React, { useState } from 'react';

/**
 * AgentTracePanel — Real-time pipeline stage visualization
 *
 * Shows a collapsible panel with pipeline stages (resolve, download, segment,
 * analyze, synthesize, dashboard) and their statuses. Each stage can show
 * agent reasoning details. Integrates with the agentTrace[] array returned
 * by GET /api/video-ingest/status/:id.
 *
 * Props:
 *   - status: string (submitted|resolving|downloading|uploaded|pipeline_queued|analyzing|complete|error)
 *   - progress: number (0-100)
 *   - message: string
 *   - agentTrace: Array<{ stage, status, detail, ts }>
 *   - gcsUri: string | null
 *   - compact: boolean (default false) — show compact inline version
 */

const STAGES = [
  { key: 'resolve', label: 'Upload', icon: '1' },
  { key: 'download', label: 'Download', icon: '2' },
  { key: 'segment', label: 'Segment', icon: '3' },
  { key: 'analyze', label: 'Analyze', icon: '4' },
  { key: 'synthesize', label: 'Synthesize', icon: '5' },
  { key: 'dashboard', label: 'Dashboard', icon: '6' },
];

const STATUS_ICONS = {
  complete: '\u2705',   // green check
  running: '\uD83D\uDD04',   // arrows (cycle)
  error: '\u274C',      // red X
  warning: '\u26A0\uFE0F',   // warning
  skipped: '\u23ED\uFE0F',   // skip
};

const STATUS_COLORS = {
  complete: '#22c55e',
  running: '#6366f1',
  error: '#ef4444',
  warning: '#f59e0b',
  skipped: '#6b7280',
};

const cardStyle = {
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 12,
  padding: 16,
};

export default function AgentTracePanel({
  status,
  progress = 0,
  message,
  agentTrace = [],
  gcsUri,
  compact = false,
}) {
  const [expanded, setExpanded] = useState(!compact);

  // Build stage status map from trace array
  const stageMap = {};
  for (const trace of agentTrace) {
    stageMap[trace.stage] = trace;
  }

  // Determine overall status color
  const overallColor = status === 'complete' ? '#22c55e'
    : status === 'error' ? '#ef4444'
    : status === 'downloading' || status === 'resolving' ? '#f59e0b'
    : status === 'uploaded' || status === 'pipeline_queued' ? '#60a5fa'
    : '#6366f1';

  if (compact && !expanded) {
    return (
      <div
        onClick={() => setExpanded(true)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          borderRadius: 8,
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.06)',
          cursor: 'pointer',
          fontSize: 12,
        }}
      >
        <span style={{ color: overallColor, fontWeight: 700 }}>
          {status?.toUpperCase() || 'PENDING'}
        </span>
        <div style={{ flex: 1, height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2 }}>
          <div style={{
            width: `${progress}%`,
            height: '100%',
            background: overallColor,
            borderRadius: 2,
            transition: 'width 0.5s ease',
          }} />
        </div>
        <span style={{ color: '#888' }}>{progress}%</span>
        <span style={{ color: '#666' }}>Expand</span>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#fff' }}>
          Analysis Pipeline
        </h4>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{
            display: 'inline-block',
            padding: '2px 10px',
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 600,
            background: `${overallColor}22`,
            color: overallColor,
            border: `1px solid ${overallColor}44`,
          }}>
            {status?.toUpperCase() || 'PENDING'}
          </span>
          {compact && (
            <button
              onClick={() => setExpanded(false)}
              style={{
                background: 'none', border: 'none', color: '#666',
                cursor: 'pointer', fontSize: 12, padding: '2px 6px',
              }}
            >
              Collapse
            </button>
          )}
        </div>
      </div>

      {/* Stage Timeline */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
        {STAGES.map((stage) => {
          const trace = stageMap[stage.key];
          const stageStatus = trace?.status || 'pending';
          const icon = STATUS_ICONS[stageStatus] || '\u2B1C'; // white square for pending
          const color = STATUS_COLORS[stageStatus] || '#444';

          return (
            <div key={stage.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minHeight: 28 }}>
              {/* Status icon */}
              <span style={{ fontSize: 14, lineHeight: '20px', minWidth: 20, textAlign: 'center' }}>
                {icon}
              </span>
              {/* Stage label + detail */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: trace ? '#fff' : '#555' }}>
                    {stage.label}
                  </span>
                  {trace?.detail && (
                    <span style={{ fontSize: 11, color: '#888' }}>
                      — {trace.detail}
                    </span>
                  )}
                </div>
                {/* Show timestamp for completed stages */}
                {trace?.ts && stageStatus === 'complete' && (
                  <div style={{ fontSize: 10, color: '#555', marginTop: 1 }}>
                    {new Date(trace.ts).toLocaleTimeString()}
                  </div>
                )}
                {/* Running stage: show expanded reasoning if available */}
                {stageStatus === 'running' && trace?.reasoning && (
                  <div style={{
                    marginTop: 4,
                    padding: '6px 10px',
                    background: 'rgba(99,102,241,0.08)',
                    borderLeft: `2px solid ${color}`,
                    borderRadius: '0 6px 6px 0',
                    fontSize: 11,
                    color: '#a5b4fc',
                    lineHeight: 1.4,
                  }}>
                    {trace.reasoning}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Progress Bar */}
      <div style={{ marginBottom: 8 }}>
        <div style={{
          height: 8,
          background: 'rgba(255,255,255,0.06)',
          borderRadius: 4,
          overflow: 'hidden',
        }}>
          <div style={{
            width: `${progress}%`,
            height: '100%',
            background: status === 'complete'
              ? 'linear-gradient(90deg, #22c55e, #16a34a)'
              : status === 'error'
                ? 'linear-gradient(90deg, #ef4444, #dc2626)'
                : 'linear-gradient(90deg, #6366f1, #8b5cf6)',
            borderRadius: 4,
            transition: 'width 0.5s ease',
          }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
          <span style={{ fontSize: 12, color: '#aaa' }}>{message || 'Waiting...'}</span>
          <span style={{ fontSize: 12, color: '#888' }}>{progress}%</span>
        </div>
      </div>

      {/* GCS URI */}
      {gcsUri && (
        <div style={{ fontSize: 11, color: '#60a5fa', marginTop: 4 }}>
          {gcsUri}
        </div>
      )}
    </div>
  );
}
